import {useMemo, useRef, useState} from 'react';
import {ApiError, errorMessageKey, newIdempotencyKey} from '../../api';
import type {Booking} from '../../api/types';
import {useRouter} from '../../app/router';
import {href, QR_PARAMS, ROUTES} from '../../app/routes';
import {
  CANCEL_WINDOW_MINUTES,
  findBookingByToken,
  isBookingCancellable,
  latestBookingForTenant,
  minutesUntil,
  statusMessageKey,
  useManagedBooking,
} from '../../booking';
import {useI18n} from '../../i18n';
import {useApi} from '../../session';
import {
  ActionLink,
  Button,
  Card,
  Screen,
  ScreenBody,
  ScreenFooter,
  StatusPill,
  type PillTone,
  Stack,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';
import {BookingDetails} from '../BookDone';

/**
 * # BookManage · view and cancel
 *
 * Route `/book/manage/:token?m=<tenant_id>`. The durable link into a booking —
 * the one BookDone tells the guest to keep. Reached with the `manage_token`, or,
 * when the URL has none, the most recent booking this phone stored for the tenant
 * (`latestBookingForTenant`), so a guest returning without the link still lands
 * on their booking.
 *
 * ## The 2-hour window, surfaced honestly
 *
 * The server enforces free cancellation only up to two hours before the booking,
 * and refuses a cancel inside that window or on a terminal booking with `409`.
 * This screen does not wait for the guest to discover that after a tap: it states
 * the rule (`book.cancelNote`), and it disables the cancel action with the reason
 * spoken aloud when the booking is **within the window** (`minutesUntil` <
 * `CANCEL_WINDOW_MINUTES`) or **not cancellable** by status. The client check is
 * a courtesy; the server is the authority, so a `409` that slips through — the
 * window closed while the page sat open — is still handled, with the same "call
 * the restaurant" answer the note gives.
 *
 * ## The existing web look
 *
 * Built from `src/ui`, sharing `BookingDetails` with BookDone. The bible draws a
 * reschedule control (`เลื่อนเวลา`) and a party-size editor; **backend #14
 * exposes no update path — only cancel** — so neither is built. Offering an edit
 * the API cannot perform would be the dishonest kind of screen this surface
 * avoids; if an update endpoint lands, it is added then.
 */
export default function BookManage() {
  const {t, language} = useI18n();
  const {params, query} = useRouter();
  const client = useApi();

  const tenantId = (query.get(QR_PARAMS.tenant) ?? '').trim() || null;
  const urlToken = (params.token ?? '').trim() || null;

  // Fall back to the last stored booking for this tenant when the URL carries no
  // token — a guest who came back without the link.
  const token = useMemo(() => {
    if (urlToken) return urlToken;
    if (!tenantId) return null;
    return latestBookingForTenant(tenantId)?.manageToken ?? null;
  }, [urlToken, tenantId]);

  const seed = useMemo<Booking | null>(() => {
    const stored = token ? findBookingByToken(token) : null;
    if (!stored) return null;
    return {
      booking_id: stored.bookingId,
      tenant_id: stored.tenantId,
      status: 'confirmed',
      name: stored.name,
      phone: stored.phone,
      party_size: stored.partySize,
      booking_time: stored.bookingTime,
      reference: stored.reference ?? null,
    };
  }, [token]);

  const managed = useManagedBooking(token, tenantId, seed);
  const {status: readStatus, booking} = managed;

  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<ApiError | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const idemRef = useRef<string | null>(null);

  const notFound = readStatus === 'notFound' && !booking;
  const loading = readStatus === 'loading' && !booking;

  const isCancelled = cancelled || booking?.status === 'cancelled';
  const cancellableStatus = booking ? isBookingCancellable(booking.status) : false;
  const mins = booking ? minutesUntil(booking.booking_time) : null;
  const withinWindow = mins !== null && mins < CANCEL_WINDOW_MINUTES;
  const cancelDisabledReason = !cancellableStatus
    ? ('book.cannotCancelTerminal' as const)
    : withinWindow
      ? ('book.cannotCancelWindow' as const)
      : null;

  async function cancel() {
    if (!token || !tenantId || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    const key = idemRef.current ?? (idemRef.current = newIdempotencyKey());
    try {
      const updated = await client.cancelPublicBooking(token, tenantId, key);
      // The server may answer with the updated booking or with no body; either
      // way the cancel succeeded, so reflect it. Prefer the server's copy.
      if (updated && typeof updated === 'object' && 'booking_id' in updated) {
        managed.set(updated);
      }
      setCancelled(true);
      setConfirming(false);
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError({kind: 'parse', message: String(caught)});
      setCancelError(failure);
    } finally {
      setCancelling(false);
    }
  }

  const title =
    booking?.reference != null
      ? t('book.manageTitleRef', {reference: booking.reference})
      : t('book.manageTitle');

  return (
    <Screen className="bookmanage">
      <style href="bookmanage" precedence="medium">
        {CSS}
      </style>

      <TopBar trailing={<LanguageToggle />} />

      {notFound ? (
        <>
          <ScreenBody center>
            <Card aria-live="polite">
              <Stack>
                <h1 className="pk-h1">{t('book.notFoundTitle')}</h1>
                <p className="pk-muted">{t('book.notFoundBody')}</p>
              </Stack>
            </Card>
          </ScreenBody>
          <ScreenFooter bordered={false}>
            {tenantId ? (
              <ActionLink
                to={href(ROUTES.book, {}, {[QR_PARAMS.tenant]: tenantId})}
                variant="secondary"
              >
                {t('book.title')}
              </ActionLink>
            ) : null}
          </ScreenFooter>
        </>
      ) : loading ? (
        <ScreenBody center>
          <p className="pk-muted" aria-live="polite">
            {t('common.loading')}
          </p>
        </ScreenBody>
      ) : booking ? (
        <ScreenBody className="bookmanage-body">
          <div className="bookmanage-head">
            <h1 className="pk-h1">{title}</h1>
            <StatusPill tone={statusTone(booking.status)}>
              {t(statusMessageKey(booking.status))}
            </StatusPill>
          </div>

          <BookingDetails booking={booking} language={language} t={t} />

          {isCancelled ? (
            <Card tone="warning" aria-live="polite">
              <Stack>
                <p className="pk-strong">{t('book.cancelledTitle')}</p>
                <p className="pk-muted">{t('book.cancelledBody')}</p>
              </Stack>
            </Card>
          ) : (
            <section className="bookmanage-cancel">
              <p className="pk-strong">{t('book.cancelHeading')}</p>
              <Card>
                <Stack>
                  <p className="pk-muted">{t('book.cancelNote')}</p>

                  {cancelError ? (
                    <p className="pk-strong" aria-live="assertive">
                      {t(
                        cancelError instanceof ApiError && cancelError.status === 409
                          ? 'book.cancelWindowError'
                          : errorMessageKey(cancelError),
                      )}
                    </p>
                  ) : null}

                  {confirming ? (
                    <Stack>
                      <p className="pk-strong">{t('book.cancelConfirmTitle')}</p>
                      <p className="pk-muted">{t('book.cancelConfirmBody')}</p>
                      <Button
                        variant="destructive"
                        disabled={cancelling}
                        disabledReason={cancelling ? t('book.cancelling') : undefined}
                        reasonVisible={false}
                        onClick={() => void cancel()}
                      >
                        {cancelling ? t('book.cancelling') : t('book.cancelConfirmYes')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={cancelling}
                        onClick={() => setConfirming(false)}
                      >
                        {t('book.keepBooking')}
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      variant="destructive"
                      disabled={cancelDisabledReason !== null}
                      disabledReason={cancelDisabledReason ? t(cancelDisabledReason) : undefined}
                      onClick={() => setConfirming(true)}
                    >
                      {t('book.cancelAction')}
                    </Button>
                  )}
                </Stack>
              </Card>
            </section>
          )}

          <p className="pk-hint bookmanage-centred">{t('common.attribution')}</p>
        </ScreenBody>
      ) : (
        <ScreenBody center>
          <p className="pk-muted" aria-live="polite">
            {t('common.loading')}
          </p>
        </ScreenBody>
      )}
    </Screen>
  );
}

function statusTone(status: string): PillTone {
  switch (status) {
    case 'confirmed':
    case 'pending':
      return 'info';
    case 'cancelled':
    case 'no_show':
      return 'warning';
    default:
      return 'neutral';
  }
}

const CSS = `
.bookmanage {
  max-width: 34rem;
  margin: 0 auto;
  width: 100%;
}

.bookmanage-body {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-4);
}

.bookmanage-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--pokpok-space-2);
}

.bookmanage-head .pk-h1 {
  margin: 0;
}

.bookmanage-cancel {
  display: flex;
  flex-direction: column;
  gap: var(--pokpok-space-2);
}

.bookmanage-centred {
  text-align: center;
}
`;
